import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Link,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/use-auth/AuthContext";
import { AuthStatus } from "../hooks/use-auth/types/AuthStatus";
import { CopyrightInfoMobile } from "./CopyrightInfoMobile";

export default function MobileLoginPage() {
    const { login, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    
    const [userLogin, setUserLogin] = useState<string>('');
    const [password, setPassword] = useState<string>('');

    const [isUserLoginValid, setIsUserLoginValid] = useState(true);
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    
    const [authStatus, setAuthStatus] = useState(AuthStatus.None);
    
    const [isProcessing, setIsProcessing] = useState(true);

    if (isUserAuthenticated()) {
        navigate('/');
        return null;
    }
    
    const handleSignIn = async (arg: any) => {
        arg.preventDefault();

        setAuthStatus(AuthStatus.None);
        setIsProcessing(true);

        if (userLogin === '')
            setIsUserLoginValid(false);

        if (password === '')
            setIsPasswordValid(false);
        
        if (!isUserLoginValid || !isPasswordValid) {
            setIsProcessing(false);
            return;
        }
        
        if (arg.target.checkValidity()) {
            const authStatus = await login({userName: userLogin, password: password});
            setAuthStatus(authStatus);
            
            if (authStatus === AuthStatus.Successful)
                navigate('/');
        }

        setIsProcessing(false);
    }

    const handleSignUp = () => {
        resetLoginStatus();
        navigate("/signup");
    }

    const resetLoginStatus = (): void => {
        if (authStatus !== AuthStatus.None)
            setAuthStatus(AuthStatus.None);
    }
    
    const handleUserLoginChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        if (isProcessing && password !== '') {
            setIsProcessing(false);
        }
        
        resetLoginStatus();
        
        setUserLogin(arg.target.value);
        setIsUserLoginValid(arg.target.validity.valid);
    }

    const handleUserLoginBlur = (arg: React.FocusEvent<HTMLInputElement>): void => {
        setUserLogin(arg.target.value);
        setIsUserLoginValid(arg.target.validity.valid);
    }

    const handlePasswordChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        if (isProcessing && userLogin !== '') {
            setIsProcessing(false);
        }
        
        resetLoginStatus();
        
        setPassword(arg.target.value);
        setIsPasswordValid(arg.target.validity.valid);
    }

    const handlePasswordBlur = (arg: React.FocusEvent<HTMLInputElement>): void => {
        setPassword(arg.target.value);
        setIsPasswordValid(arg.target.validity.valid);
    }
    
    const errorColour = '#cc2626';
    const backgroundColour = '#E7ECEE';

    return (
        <Stack
            direction="column"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
                backgroundColor: 'primary.main',
                minHeight: '100vh',
            }}
        >
            <Dialog
                open={true}
                PaperProps={{
                    onSubmit: handleSignIn,
                    sx: {
                        width: '85%',
                    },
                }}
                style={{
                    opacity: '1',
                    position: 'absolute', left: '0%', top: '-10%',
                }}
            >
                <Box
                    component="form"
                    noValidate
                    sx={{
                        backgroundColor: backgroundColour
                    }}
                >
                    <DialogContent sx={{ padding: '16px', paddingBottom: '8px'}}>
                        <Stack
                            direction="column"
                            sx={{
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '24px',
                                    fontWeight: '750',
                                }}
                            >
                                Log In
                            </Typography>
                            <Divider
                                sx={{
                                    marginTop: "18px",
                                    marginBottom: "22px",
                                    width: '100%',
                                    borderColor: '#5a5a5a',
                                    borderWidth: '0.75px'
                                }}
                            />
                            <TextField
                                focused
                                required
                                margin="normal"
                                id="login"
                                name="login"
                                label="Login"
                                type="text"
                                variant="outlined"
                                size="small"
                                defaultValue=""
                                onChange={handleUserLoginChanging}
                                onBlur={handleUserLoginBlur}
                                fullWidth
                                error={!isUserLoginValid}
                            />
                            <TextField
                                focused
                                required
                                margin="normal"
                                id="password"
                                name="password"
                                label="Password"
                                type="password"
                                variant="outlined"
                                size="small"
                                defaultValue=""
                                onChange={handlePasswordChanging}
                                onBlur={handlePasswordBlur}
                                fullWidth
                                error={!isPasswordValid}
                            />
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Divider
                                    sx={{
                                        marginTop: '25px',
                                        width: '100%',
                                        userSelect: 'none',
                                        borderColor:
                                            (authStatus === AuthStatus.None ||
                                                authStatus === AuthStatus.Successful)
                                                ? '#7f7f7f'
                                                : errorColour,
                                        borderWidth: '0.75px',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        marginTop: '10px',
                                        userSelect: 'none',
                                        color:
                                            (authStatus === AuthStatus.None ||
                                                authStatus === AuthStatus.Successful)
                                                ? backgroundColour
                                                : errorColour,
                                    }}
                                >
                                    {
                                        authStatus === AuthStatus.None
                                            ? "User credential validation failure"
                                            : authStatus === AuthStatus.Successful
                                                ? "Confirmed!"
                                                : authStatus === AuthStatus.Failed
                                                    ? 'Failed to login. Check your credentials'
                                                    : 'Network or server error'
                                    }
                                </Typography>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: '16px',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Stack
                            direction="column"
                            sx={{
                                marginBottom: '15px',
                                justifyContent: 'flex-start',
                                width: '100%',
                            }}
                        >
                            <Button
                                sx={{
                                    fontWeight: "bold",
                                    width: '100%',
                                }}
                                size="small"
                                variant="contained"
                                color="primary"
                                type="submit"
                                {...(isProcessing
                                    ? { disabled: true }
                                    : {})}
                            >
                                Continue
                            </Button>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={handleSignUp}
                                color="primary"
                                sx={{
                                    fontWeight: "bold",
                                    marginTop: '25px',
                                    alignSelf: 'flex-start',
                                    alignContent: 'left',
                                }}
                            >
                                Create an account
                            </Link>
                        </Stack>
                    </DialogActions>
                </Box>
            </Dialog>
            <CopyrightInfoMobile />
        </Stack>
    )
}